import React, { useState, useEffect } from 'react';
import { 
  Settings, Save, Globe, Mail, CreditCard, Bell, 
  Shield, Database, RefreshCw, Check, AlertTriangle, Award, Plus, Trash2, Star
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { useAdminAuth } from '../context/AdminAuthContext';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/utils';

const AdminSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  
  // Settings state
  const [generalSettings, setGeneralSettings] = useState({
    site_name: 'One Globe Alliance',
    tagline: 'Global Logistics Network',
    support_email: 'info@oneglobealliance.com',
    contact_phone: '+91 989-210-5006',
    address: '',
    timezone: 'UTC',
    date_format: 'YYYY-MM-DD',
    currency: 'USD'
  });
  
  const [emailSettings, setEmailSettings] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    from_email: 'noreply@oneglobealliance.com',
    from_name: 'One Globe Alliance',
    email_footer: ''
  });
  
  const [membershipSettings, setMembershipSettings] = useState({
    auto_approve_members: false,
    require_payment_proof: true,
    payment_grace_days: 7,
    trial_period_days: 30,
    max_colleagues_per_member: 10,
    default_points_on_join: 100,
    referral_bonus_points: 500
  });
  
  const [notificationSettings, setNotificationSettings] = useState({
    notify_new_member: true,
    notify_payment_received: true,
    notify_quote_activity: true,
    notify_event_registration: true,
    admin_email_notifications: true,
    digest_frequency: 'daily'
  });
  
  const [securitySettings, setSecuritySettings] = useState({
    session_timeout_hours: 24,
    max_login_attempts: 5,
    lockout_duration_minutes: 30,
    require_2fa_admin: false,
    password_min_length: 8,
    password_require_special: true
  });

  const DEFAULT_RATING_CRITERIA = [
    { name: 'Response Time', weight: 25, description: 'Average time to respond to quotes and messages', levels: { excellent: '< 2 hours', good: '2-6 hours', fair: '6-24 hours' } },
    { name: 'Deal Size', weight: 20, description: 'Total value of deals completed', levels: null },
    { name: 'Number of Deals', weight: 20, description: 'Total number of successful deals completed', levels: null },
    { name: 'Member Ratings', weight: 25, description: 'Average ratings received from other members after deal completion', levels: null },
    { name: 'Completion Rate', weight: 10, description: 'Percentage of nominated deals that were successfully completed', levels: null }
  ];
  
  const [ratingCriteriaSettings, setRatingCriteriaSettings] = useState({ criteria: [] });
  const [showAddCriteriaDialog, setShowAddCriteriaDialog] = useState(false);
  const [newCriterionForm, setNewCriterionForm] = useState({ name: '', weight: 0, description: '' });
  const [showRemoveConfirmDialog, setShowRemoveConfirmDialog] = useState(false);
  const [criterionToRemove, setCriterionToRemove] = useState(null);
  
  const { api, hasPermission } = useAdminAuth();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/settings');
      const settings = response.data.settings || {};
      
      if (settings.general) setGeneralSettings({ ...generalSettings, ...settings.general });
      if (settings.email) setEmailSettings({ ...emailSettings, ...settings.email });
      if (settings.membership) setMembershipSettings({ ...membershipSettings, ...settings.membership });
      if (settings.notifications) setNotificationSettings({ ...notificationSettings, ...settings.notifications });
      if (settings.security) setSecuritySettings({ ...securitySettings, ...settings.security });
      if (settings.rating_criteria?.criteria?.length) {
        setRatingCriteriaSettings({ criteria: settings.rating_criteria.criteria });
      } else {
        setRatingCriteriaSettings({ criteria: [...DEFAULT_RATING_CRITERIA] });
      }
    } catch (error) {
      // Use default settings if API fails
      console.log('Using default settings');
      setRatingCriteriaSettings({ criteria: [...DEFAULT_RATING_CRITERIA] });
    } finally {
      setLoading(false);
    }
  };

  const formatCategoryLabel = (cat) => {
    return cat.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const handleSave = async (category, data) => {
    try {
      setSaving(true);
      await api.put('/api/admin/settings', { category, settings: data });
      toast.success(`${formatCategoryLabel(category)} settings saved`);
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to save settings'));
    } finally {
      setSaving(false);
    }
  };

  const canEdit = hasPermission('settings', 'edit');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-settings">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">System Settings</h1>
          <p className="text-gray-500">Configure platform settings and preferences</p>
        </div>
        <Button variant="outline" onClick={fetchSettings}>
          <RefreshCw size={16} className="mr-2" />
          Refresh
        </Button>
      </div>

      {/* Settings Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex flex-wrap gap-1 w-full max-w-3xl">
          <TabsTrigger value="general" className="flex items-center gap-2 shrink-0">
            <Globe size={16} className="shrink-0" />
            General
          </TabsTrigger>
          <TabsTrigger value="email" className="flex items-center gap-2 shrink-0">
            <Mail size={16} className="shrink-0" />
            Email
          </TabsTrigger>
          <TabsTrigger value="membership" className="flex items-center gap-2 shrink-0">
            <CreditCard size={16} className="shrink-0" />
            Membership
          </TabsTrigger>
          <TabsTrigger value="rating_criteria" className="flex items-center gap-2 shrink-0">
            <Star size={16} className="shrink-0" />
            Rating Criteria
          </TabsTrigger>
          <TabsTrigger value="notifications" className="flex items-center gap-2 shrink-0">
            <Bell size={16} className="shrink-0" />
            Notifications
          </TabsTrigger>
          <TabsTrigger value="security" className="flex items-center gap-2 shrink-0">
            <Shield size={16} className="shrink-0" />
            Security
          </TabsTrigger>
        </TabsList>

        {/* General Settings */}
        <TabsContent value="general">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Globe size={20} className="mr-2 text-blue-600" />
                General Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Site Name</Label>
                  <Input
                    value={generalSettings.site_name}
                    onChange={(e) => setGeneralSettings({ ...generalSettings, site_name: e.target.value })}
                    disabled={!canEdit}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Tagline</Label>
                  <Input
                    value={generalSettings.tagline}
                    onChange={(e) => setGeneralSettings({ ...generalSettings, tagline: e.target.value })}
                    disabled={!canEdit}
                    className="mt-1"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Support Email</Label>
                  <Input
                    type="email"
                    value={generalSettings.support_email}
                    onChange={(e) => setGeneralSettings({ ...generalSettings, support_email: e.target.value })}
                    disabled={!canEdit}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Contact Phone</Label>
                  <Input
                    value={generalSettings.contact_phone}
                    onChange={(e) => setGeneralSettings({ ...generalSettings, contact_phone: e.target.value })}
                    disabled={!canEdit}
                    className="mt-1"
                  />
                </div>
              </div>
              
              <div>
                <Label>Address</Label>
                <Textarea
                  value={generalSettings.address}
                  onChange={(e) => setGeneralSettings({ ...generalSettings, address: e.target.value })}
                  disabled={!canEdit}
                  rows={2}
                  className="mt-1"
                />
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Timezone</Label>
                  <select
                    value={generalSettings.timezone}
                    onChange={(e) => setGeneralSettings({ ...generalSettings, timezone: e.target.value })}
                    disabled={!canEdit}
                    className="w-full mt-1 px-3 py-2 border rounded-md"
                  >
                    <option value="UTC">UTC</option>
                    <option value="America/New_York">Eastern Time</option>
                    <option value="America/Los_Angeles">Pacific Time</option>
                    <option value="Europe/London">London</option>
                    <option value="Asia/Singapore">Singapore</option>
                    <option value="Asia/Dubai">Dubai</option>
                  </select>
                </div>
                <div>
                  <Label>Date Format</Label>
                  <select
                    value={generalSettings.date_format}
                    onChange={(e) => setGeneralSettings({ ...generalSettings, date_format: e.target.value })}
                    disabled={!canEdit}
                    className="w-full mt-1 px-3 py-2 border rounded-md"
                  >
                    <option value="YYYY-MM-DD">2026-01-26</option>
                    <option value="DD/MM/YYYY">26/01/2026</option>
                    <option value="MM/DD/YYYY">01/26/2026</option>
                  </select>
                </div>
                <div>
                  <Label>Default Currency</Label>
                  <select
                    value={generalSettings.currency}
                    onChange={(e) => setGeneralSettings({ ...generalSettings, currency: e.target.value })}
                    disabled={!canEdit}
                    className="w-full mt-1 px-3 py-2 border rounded-md"
                  >
                    <option value="USD">USD ($)</option>
                    <option value="EUR">EUR (€)</option>
                    <option value="GBP">GBP (£)</option>
                    <option value="SGD">SGD (S$)</option>
                    <option value="AED">AED (د.إ)</option>
                  </select>
                </div>
              </div>
              
              {canEdit && (
                <div className="flex justify-end pt-4 border-t">
                  <Button 
                    onClick={() => handleSave('general', generalSettings)}
                    disabled={saving}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <Save size={16} className="mr-2" />
                    {saving ? 'Saving...' : 'Save General Settings'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Email Settings */}
        <TabsContent value="email">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Mail size={20} className="mr-2 text-purple-600" />
                Email Configuration
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-3 bg-yellow-50 rounded-lg text-sm text-yellow-700">
                <AlertTriangle size={16} className="inline mr-2" />
                Email sending requires integration with an email service (SendGrid, AWS SES, etc.)
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>SMTP Host</Label>
                  <Input
                    value={emailSettings.smtp_host}
                    onChange={(e) => setEmailSettings({ ...emailSettings, smtp_host: e.target.value })}
                    placeholder="smtp.sendgrid.net"
                    disabled={!canEdit}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>SMTP Port</Label>
                  <Input
                    type="number"
                    value={emailSettings.smtp_port}
                    onChange={(e) => setEmailSettings({ ...emailSettings, smtp_port: parseInt(e.target.value) })}
                    disabled={!canEdit}
                    className="mt-1"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>SMTP Username</Label>
                  <Input
                    value={emailSettings.smtp_username}
                    onChange={(e) => setEmailSettings({ ...emailSettings, smtp_username: e.target.value })}
                    disabled={!canEdit}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>SMTP Password</Label>
                  <Input
                    type="password"
                    value={emailSettings.smtp_password}
                    onChange={(e) => setEmailSettings({ ...emailSettings, smtp_password: e.target.value })}
                    placeholder="••••••••"
                    disabled={!canEdit}
                    className="mt-1"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>From Email</Label>
                  <Input
                    type="email"
                    value={emailSettings.from_email}
                    onChange={(e) => setEmailSettings({ ...emailSettings, from_email: e.target.value })}
                    disabled={!canEdit}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>From Name</Label>
                  <Input
                    value={emailSettings.from_name}
                    onChange={(e) => setEmailSettings({ ...emailSettings, from_name: e.target.value })}
                    disabled={!canEdit}
                    className="mt-1"
                  />
                </div>
              </div>
              
              <div>
                <Label>Email Footer</Label>
                <Textarea
                  value={emailSettings.email_footer}
                  onChange={(e) => setEmailSettings({ ...emailSettings, email_footer: e.target.value })}
                  placeholder="Unsubscribe links, company info, etc."
                  disabled={!canEdit}
                  rows={3}
                  className="mt-1"
                />
              </div>
              
              {canEdit && (
                <div className="flex justify-end pt-4 border-t">
                  <Button 
                    onClick={() => handleSave('email', emailSettings)}
                    disabled={saving}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    <Save size={16} className="mr-2" />
                    {saving ? 'Saving...' : 'Save Email Settings'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Membership Settings */}
        <TabsContent value="membership">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <CreditCard size={20} className="mr-2 text-green-600" />
                Membership Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={membershipSettings.auto_approve_members}
                      onChange={(e) => setMembershipSettings({ ...membershipSettings, auto_approve_members: e.target.checked })}
                      disabled={!canEdit}
                      className="w-4 h-4"
                    />
                    <span>Auto-approve new members</span>
                  </label>
                  
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={membershipSettings.require_payment_proof}
                      onChange={(e) => setMembershipSettings({ ...membershipSettings, require_payment_proof: e.target.checked })}
                      disabled={!canEdit}
                      className="w-4 h-4"
                    />
                    <span>Require payment proof upload</span>
                  </label>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <Label>Payment Grace Period (days)</Label>
                    <Input
                      type="number"
                      value={membershipSettings.payment_grace_days}
                      onChange={(e) => setMembershipSettings({ ...membershipSettings, payment_grace_days: parseInt(e.target.value) })}
                      disabled={!canEdit}
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label>Trial Period (days)</Label>
                    <Input
                      type="number"
                      value={membershipSettings.trial_period_days}
                      onChange={(e) => setMembershipSettings({ ...membershipSettings, trial_period_days: parseInt(e.target.value) })}
                      disabled={!canEdit}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4 pt-4 border-t">
                <div>
                  <Label>Max Colleagues per Member</Label>
                  <Input
                    type="number"
                    value={membershipSettings.max_colleagues_per_member}
                    onChange={(e) => setMembershipSettings({ ...membershipSettings, max_colleagues_per_member: parseInt(e.target.value) })}
                    disabled={!canEdit}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Default Points on Join</Label>
                  <Input
                    type="number"
                    value={membershipSettings.default_points_on_join}
                    onChange={(e) => setMembershipSettings({ ...membershipSettings, default_points_on_join: parseInt(e.target.value) })}
                    disabled={!canEdit}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Referral Bonus Points</Label>
                  <Input
                    type="number"
                    value={membershipSettings.referral_bonus_points}
                    onChange={(e) => setMembershipSettings({ ...membershipSettings, referral_bonus_points: parseInt(e.target.value) })}
                    disabled={!canEdit}
                    className="mt-1"
                  />
                </div>
              </div>
              
              {canEdit && (
                <div className="flex justify-end pt-4 border-t">
                  <Button 
                    onClick={() => handleSave('membership', membershipSettings)}
                    disabled={saving}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    <Save size={16} className="mr-2" />
                    {saving ? 'Saving...' : 'Save Membership Settings'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Rating Criteria Settings */}
        <TabsContent value="rating_criteria">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Award size={20} className="mr-2 text-amber-600" />
                Rating Criteria
              </CardTitle>
              <p className="text-sm text-gray-500 mt-1">
                Configure the criteria used to calculate member ratings. Shown in the member directory when users click &quot;Rating Criteria&quot;. Sum of all weights must equal 100%.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium">
                  Total: {ratingCriteriaSettings.criteria.reduce((s, c) => s + (c.weight || 0), 0)}%
                  {ratingCriteriaSettings.criteria.reduce((s, c) => s + (c.weight || 0), 0) !== 100 && (
                    <span className="ml-2 text-amber-600">(Must equal 100%)</span>
                  )}
                </div>
                {canEdit && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setNewCriterionForm({ name: '', weight: 0, description: '' });
                      setShowAddCriteriaDialog(true);
                    }}
                  >
                    <Plus size={16} className="mr-1" />
                    Add Criteria
                  </Button>
                )}
              </div>
              <div className="space-y-3">
                {ratingCriteriaSettings.criteria.map((criterion, idx) => (
                  <div key={idx} className="p-4 border rounded-lg bg-gray-50/50 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <Label>Name</Label>
                        <Input
                          value={criterion.name}
                          onChange={(e) => {
                            const next = [...ratingCriteriaSettings.criteria];
                            next[idx] = { ...next[idx], name: e.target.value };
                            setRatingCriteriaSettings({ criteria: next });
                          }}
                          placeholder="e.g. Response Time"
                          disabled={!canEdit}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Weight (%)</Label>
                        <Input
                          type="number"
                          min={0}
                          max={100}
                          value={criterion.weight}
                          onChange={(e) => {
                            const next = [...ratingCriteriaSettings.criteria];
                            next[idx] = { ...next[idx], weight: parseInt(e.target.value) || 0 };
                            setRatingCriteriaSettings({ criteria: next });
                          }}
                          disabled={!canEdit}
                          className="mt-1"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Input
                        value={criterion.description}
                        onChange={(e) => {
                          const next = [...ratingCriteriaSettings.criteria];
                          next[idx] = { ...next[idx], description: e.target.value };
                          setRatingCriteriaSettings({ criteria: next });
                        }}
                        placeholder="e.g. Average time to respond to quotes and messages"
                        disabled={!canEdit}
                        className="mt-1"
                      />
                    </div>
                    {canEdit && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => {
                          setCriterionToRemove(idx);
                          setShowRemoveConfirmDialog(true);
                        }}
                      >
                        <Trash2 size={14} className="mr-1" />
                        Remove
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              {canEdit && (
                <div className="flex justify-end pt-4 border-t">
                  <Button
                    onClick={() => {
                      const total = ratingCriteriaSettings.criteria.reduce((s, c) => s + (c.weight || 0), 0);
                      if (ratingCriteriaSettings.criteria.length === 0) {
                        toast.error('Add at least one rating criterion');
                        return;
                      }
                      if (total !== 100) {
                        toast.error('Sum of all criteria weights must equal 100%');
                        return;
                      }
                      handleSave('rating_criteria', ratingCriteriaSettings);
                    }}
                    disabled={saving || ratingCriteriaSettings.criteria.length === 0 || ratingCriteriaSettings.criteria.reduce((s, c) => s + (c.weight || 0), 0) !== 100}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                  >
                    <Save size={16} className="mr-2" />
                    {saving ? 'Saving...' : 'Save Rating Criteria'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Remove Criteria Confirmation Dialog */}
          <Dialog open={showRemoveConfirmDialog} onOpenChange={setShowRemoveConfirmDialog}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Remove Rating Criterion</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-gray-600 py-2">
                Are you sure you want to remove &quot;{criterionToRemove !== null ? ratingCriteriaSettings.criteria[criterionToRemove]?.name : ''}&quot;? This action cannot be undone until you save.
              </p>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setShowRemoveConfirmDialog(false); setCriterionToRemove(null); }}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  className="bg-red-600 hover:bg-red-700 text-white"
                  onClick={() => {
                    if (criterionToRemove !== null) {
                      setRatingCriteriaSettings({
                        criteria: ratingCriteriaSettings.criteria.filter((_, i) => i !== criterionToRemove)
                      });
                    }
                    setShowRemoveConfirmDialog(false);
                    setCriterionToRemove(null);
                  }}
                >
                  Remove
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Add Criteria Dialog */}
          <Dialog open={showAddCriteriaDialog} onOpenChange={setShowAddCriteriaDialog}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Add Rating Criterion</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div>
                  <Label>Name</Label>
                  <Input
                    value={newCriterionForm.name}
                    onChange={(e) => setNewCriterionForm({ ...newCriterionForm, name: e.target.value })}
                    placeholder="e.g. Response Time"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Weight (%)</Label>
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    value={newCriterionForm.weight}
                    onChange={(e) => setNewCriterionForm({ ...newCriterionForm, weight: parseInt(e.target.value) || 0 })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Input
                    value={newCriterionForm.description}
                    onChange={(e) => setNewCriterionForm({ ...newCriterionForm, description: e.target.value })}
                    placeholder="e.g. Average time to respond to quotes and messages"
                    className="mt-1"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowAddCriteriaDialog(false)}>
                  Cancel
                </Button>
                <Button
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  onClick={() => {
                    if (!newCriterionForm.name?.trim()) {
                      toast.error('Please enter a criterion name');
                      return;
                    }
                    setRatingCriteriaSettings({
                      criteria: [...ratingCriteriaSettings.criteria, { ...newCriterionForm, levels: null }]
                    });
                    setShowAddCriteriaDialog(false);
                    setNewCriterionForm({ name: '', weight: 0, description: '' });
                  }}
                >
                  Add Criteria
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Notification Settings */}
        <TabsContent value="notifications">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Bell size={20} className="mr-2 text-amber-600" />
                Notification Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4">
                <h4 className="font-medium">Admin Notifications</h4>
                
                <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer">
                  <div>
                    <p className="font-medium">New Member Registration</p>
                    <p className="text-sm text-gray-500">Notify when a new member registers</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={notificationSettings.notify_new_member}
                    onChange={(e) => setNotificationSettings({ ...notificationSettings, notify_new_member: e.target.checked })}
                    disabled={!canEdit}
                    className="w-5 h-5"
                  />
                </label>
                
                <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer">
                  <div>
                    <p className="font-medium">Payment Received</p>
                    <p className="text-sm text-gray-500">Notify when payment proof is uploaded</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={notificationSettings.notify_payment_received}
                    onChange={(e) => setNotificationSettings({ ...notificationSettings, notify_payment_received: e.target.checked })}
                    disabled={!canEdit}
                    className="w-5 h-5"
                  />
                </label>
                
                <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer">
                  <div>
                    <p className="font-medium">Quote Activity</p>
                    <p className="text-sm text-gray-500">Notify on high-value quote activity</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={notificationSettings.notify_quote_activity}
                    onChange={(e) => setNotificationSettings({ ...notificationSettings, notify_quote_activity: e.target.checked })}
                    disabled={!canEdit}
                    className="w-5 h-5"
                  />
                </label>
                
                <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer">
                  <div>
                    <p className="font-medium">Event Registration</p>
                    <p className="text-sm text-gray-500">Notify when members register for events</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={notificationSettings.notify_event_registration}
                    onChange={(e) => setNotificationSettings({ ...notificationSettings, notify_event_registration: e.target.checked })}
                    disabled={!canEdit}
                    className="w-5 h-5"
                  />
                </label>
              </div>
              
              <div className="pt-4 border-t">
                <div>
                  <Label>Email Digest Frequency</Label>
                  <select
                    value={notificationSettings.digest_frequency}
                    onChange={(e) => setNotificationSettings({ ...notificationSettings, digest_frequency: e.target.value })}
                    disabled={!canEdit}
                    className="w-full mt-1 px-3 py-2 border rounded-md max-w-xs"
                  >
                    <option value="realtime">Real-time</option>
                    <option value="hourly">Hourly</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="never">Never</option>
                  </select>
                </div>
              </div>
              
              {canEdit && (
                <div className="flex justify-end pt-4 border-t">
                  <Button 
                    onClick={() => handleSave('notifications', notificationSettings)}
                    disabled={saving}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                  >
                    <Save size={16} className="mr-2" />
                    {saving ? 'Saving...' : 'Save Notification Settings'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Settings */}
        <TabsContent value="security">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Shield size={20} className="mr-2 text-red-600" />
                Security Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Session Timeout (hours)</Label>
                  <Input
                    type="number"
                    value={securitySettings.session_timeout_hours}
                    onChange={(e) => setSecuritySettings({ ...securitySettings, session_timeout_hours: parseInt(e.target.value) })}
                    disabled={!canEdit}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Max Login Attempts</Label>
                  <Input
                    type="number"
                    value={securitySettings.max_login_attempts}
                    onChange={(e) => setSecuritySettings({ ...securitySettings, max_login_attempts: parseInt(e.target.value) })}
                    disabled={!canEdit}
                    className="mt-1"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Lockout Duration (minutes)</Label>
                  <Input
                    type="number"
                    value={securitySettings.lockout_duration_minutes}
                    onChange={(e) => setSecuritySettings({ ...securitySettings, lockout_duration_minutes: parseInt(e.target.value) })}
                    disabled={!canEdit}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Min Password Length</Label>
                  <Input
                    type="number"
                    value={securitySettings.password_min_length}
                    onChange={(e) => setSecuritySettings({ ...securitySettings, password_min_length: parseInt(e.target.value) })}
                    disabled={!canEdit}
                    className="mt-1"
                  />
                </div>
              </div>
              
              <div className="space-y-4 pt-4 border-t">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={securitySettings.require_2fa_admin}
                    onChange={(e) => setSecuritySettings({ ...securitySettings, require_2fa_admin: e.target.checked })}
                    disabled={!canEdit}
                    className="w-4 h-4"
                  />
                  <span>Require 2FA for admin accounts</span>
                </label>
                
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={securitySettings.password_require_special}
                    onChange={(e) => setSecuritySettings({ ...securitySettings, password_require_special: e.target.checked })}
                    disabled={!canEdit}
                    className="w-4 h-4"
                  />
                  <span>Require special characters in passwords</span>
                </label>
              </div>
              
              {canEdit && (
                <div className="flex justify-end pt-4 border-t">
                  <Button 
                    onClick={() => handleSave('security', securitySettings)}
                    disabled={saving}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    <Save size={16} className="mr-2" />
                    {saving ? 'Saving...' : 'Save Security Settings'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminSettings;
